import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, CheckCircle2, Clock, Download, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Última corrida de un monitoreo (perfil transaccional o efectivo/relevantes) con su
 * constancia sellada y el historial descargable. El historial no se borra.
 */
export default function UltimaCorridaCard({ modulo, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [historial, setHistorial] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: r } = await api.get(`/pld/monitoreo/corridas?modulo=${modulo}&limit=60`);
      setData(r && typeof r === "object" && !Array.isArray(r) ? r : null);
    } catch (e) {
      setData(null);
      if (![401, 403].includes(e?.response?.status)) toast.error(apiError(e));
    } finally {
      setCargando(false);
    }
  }, [modulo]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const descargar = async (id, folio) => {
    try {
      const res = await api.get(`/pld/monitoreo/corridas/${id}/constancia`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `constancia_monitoreo_${folio || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const verDetalle = async (id) => {
    try {
      const { data: r } = await api.get(`/pld/monitoreo/corridas/${id}/detalles`);
      setDetalle(r);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (cargando) {
    return (
      <div className="animate-pulse rounded-lg border border-slate-200 bg-white px-4 py-3 text-xs text-slate-400"
           data-testid={`corrida-${modulo}-cargando`}>
        Cargando última corrida…
      </div>
    );
  }
  if (!data) return null;

  const u = data.ultimo;
  const limpio = u?.resultado === "sin_desviaciones";

  return (
    <div data-testid={`corrida-${modulo}-card`}>
      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
        !u ? "border-slate-200 bg-slate-50"
          : limpio ? "border-emerald-200 bg-emerald-50"
            : "border-amber-300 bg-amber-50"
      }`}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            {!u ? <Clock className="h-5 w-5 text-slate-400" />
              : limpio ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                : <AlertTriangle className="h-5 w-5 text-amber-600" />}
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Última corrida</div>
            {!u ? (
              <div className="text-xs text-slate-600" data-testid={`corrida-${modulo}-vacio`}>
                Todavía no hay corridas registradas. Corre el monitoreo o espera la corrida
                automática de la noche ({data.ventana_automatico}).
              </div>
            ) : (
              <>
                <div className="text-xs text-slate-700" data-testid={`corrida-${modulo}-resumen`}>
                  <b data-testid={`corrida-${modulo}-fecha`}>{u.ejecutado_en}</b>
                  {" — "}
                  <b data-testid={`corrida-${modulo}-resultado`}>{u.resultado_titulo}</b>
                  {" · "}{u.clientes_evaluados} cliente(s) evaluado(s)
                  {" · "}{u.tipo === "automatico" ? "automática" : "manual"}
                  {" · "}{u.usuario}
                </div>
                <div className="mt-0.5 text-[11px] text-slate-500" data-testid={`corrida-${modulo}-folio`}>
                  Folio {u.folio} · sello SHA-256 {String(u.sello_sha256 || "").slice(0, 16)}…
                </div>
                {u.aviso && (
                  <div className="mt-1 text-[11px] font-medium text-amber-700"
                       data-testid={`corrida-${modulo}-aviso`}>{u.aviso}</div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {u && u.total_hallazgos > 0 && (
            <Button size="sm" variant="outline" onClick={() => verDetalle(u.id)}
                    data-testid={`corrida-${modulo}-btn-detalle`}>
              Ver hallazgos
            </Button>
          )}
          {u && (
            <Button size="sm" onClick={() => descargar(u.id, u.folio)}
                    data-testid={`corrida-${modulo}-btn-constancia`}
                    className="bg-primary hover:bg-sidebar-hover">
              <Download className="mr-1 h-3.5 w-3.5" /> Constancia PDF
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setHistorial(true)}
                  data-testid={`corrida-${modulo}-btn-historial`}>
            <FileText className="mr-1 h-3.5 w-3.5" /> Historial
          </Button>
        </div>
      </div>

      <Dialog open={historial} onOpenChange={setHistorial}>
        <DialogContent className="max-w-4xl" data-testid={`dialog-corridas-${modulo}`}>
          <DialogHeader>
            <DialogTitle>Historial · {data.modulo_nombre}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {!data.historial?.length ? (
              <div className="py-6 text-center text-sm text-slate-500">Sin corridas registradas.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Folio</TableHead>
                    <TableHead>Fecha y hora</TableHead>
                    <TableHead>Modalidad</TableHead>
                    <TableHead>Resultado</TableHead>
                    <TableHead className="text-right">Clientes</TableHead>
                    <TableHead className="text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.historial.map((c) => (
                    <TableRow key={c.id} data-testid={`fila-corrida-${c.id}`}>
                      <TableCell className="font-mono text-xs">{c.folio}</TableCell>
                      <TableCell className="text-sm">{c.ejecutado_en}</TableCell>
                      <TableCell className="text-xs">
                        {c.tipo === "automatico" ? "Automática" : "Manual"}
                        <span className="block text-[11px] text-slate-400">{c.usuario}</span>
                      </TableCell>
                      <TableCell>
                        {c.resultado === "sin_desviaciones"
                          ? <Badge className="bg-emerald-100 text-emerald-700">Sin desviaciones</Badge>
                          : <Badge className="bg-amber-100 text-amber-700">{c.total_hallazgos} hallazgo(s)</Badge>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{c.clientes_evaluados}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => descargar(c.id, c.folio)}
                                data-testid={`hist-corrida-pdf-${c.id}`}>
                          <Download className="mr-1 h-3 w-3" /> PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistorial(false)}
                    data-testid={`dialog-corridas-${modulo}-cerrar`}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detalle} onOpenChange={(v) => !v && setDetalle(null)}>
        <DialogContent className="max-w-4xl" data-testid={`dialog-hallazgos-${modulo}`}>
          <DialogHeader>
            <DialogTitle>Hallazgos de la corrida {detalle?.folio}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {detalle && (
              <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs"
                   data-testid={`hallazgos-${modulo}-parametros`}>
                <p className="mb-1 font-semibold uppercase tracking-wide text-slate-500">
                  Parámetros usados
                </p>
                <ul className="grid grid-cols-1 gap-x-6 gap-y-0.5 sm:grid-cols-2">
                  {(detalle.parametros || []).map((p) => (
                    <li key={p.etiqueta} className="flex justify-between gap-3">
                      <span className="text-slate-600">{p.etiqueta}</span>
                      <span className="font-medium text-slate-800">{p.valor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!detalle?.hallazgos?.length ? (
              <div className="py-6 text-center text-sm text-slate-500">Sin desviaciones.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {(detalle.hallazgos_columnas || []).map((c) => (
                      <TableHead key={c}>{c}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detalle.hallazgos.map((fila, i) => (
                    <TableRow key={i} data-testid={`fila-hallazgo-${i}`}>
                      {fila.map((v, j) => (
                        <TableCell key={j} className="text-xs">{v}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalle(null)}
                    data-testid={`dialog-hallazgos-${modulo}-cerrar`}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
